import type { ISODate } from '../contract.js';
import { copy } from '../copy.js';
import { compareDates, dateLong, gridPos, isoOf, monthFromKey, monthGrid, monthKey, monthOf } from '../dates.js';
import { monthStatus, postersCount, stripeOf, todayOf } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { color, shadow } from '../tokens.js';

/** The calendar layer, ported from `calVals`, `calCell` and `tileLook` (02 §3.1, 03 §3.2–3.3). */

interface Anim { readonly op: number; readonly tr: string }
export type CalCell =
  | ({ readonly kind: 'out' } & Anim)
  | ({ readonly kind: 'future'; readonly label: string } & Anim)
  | { readonly kind: 'wait'; readonly loading: boolean }
  | ({ readonly kind: 'open'; readonly date: ISODate; readonly label: string; readonly today: boolean; readonly bg: string; readonly shadow: string; readonly outline: string; readonly segs: readonly string[] } & Anim);

export interface CalendarVals {
  readonly show: boolean;
  readonly cells: readonly CalCell[];
  readonly status: { readonly show: boolean; readonly retry: boolean; readonly text: string };
}

const IDLE = 'background-color .14s ease, box-shadow .24s ease';

/** `tileLook`: how an open tile looks, hovered or not. */
export function tileLook(st: AppState, date: ISODate, hovered: boolean): { readonly bg: string; readonly shadow: string; readonly outline: string; readonly segs: readonly string[] } {
  const isToday = date === todayOf(st);
  const bg = isToday ? (hovered ? color.tileTodayHover : color.tileToday) : (hovered ? color.tileHover : color.tile);
  const sh = isToday ? (hovered ? shadow.todayHaloHover : shadow.todayHalo) : (hovered ? `inset 0 0 0 1px ${color.lineStrong}` : 'inset 0 0 0 1px rgba(255, 255, 255, 0.03)');
  return { bg, shadow: sh, outline: isToday ? `2px solid ${color.paper}` : '2px solid transparent', segs: stripeOf(st, date) };
}

export function calendarVals(st: AppState): CalendarVals {
  const ym = monthFromKey(st.month), M = monthGrid(ym), state = monthStatus(st, st.month), today = todayOf(st);
  const phase = st.cd;
  const selIdx = st.cd && monthKey(monthOf(st.date)) === st.month ? gridPos(st.date).i : -1;
  const anim = (i: number): Anim => {
    if (!phase) return { op: 1, tr: IDLE };
    const isSel = i === selIdx;
    const dist = selIdx >= 0 ? Math.max(Math.abs(i % 7 - selIdx % 7), Math.abs(Math.floor(i / 7) - Math.floor(selIdx / 7))) : Math.abs(i % 7 - 3) + Math.floor(i / 7);
    if (phase === 'in-pre') return { op: isSel ? 0 : 1, tr: 'none' };
    if (phase === 'in') return { op: 0, tr: `opacity 240ms ease ${dist * 24}ms, transform 320ms ease ${dist * 24}ms` };
    if (phase === 'out-pre') return { op: 0, tr: 'none' };
    return { op: isSel ? 0 : 1, tr: `opacity 300ms ease ${140 + dist * 24}ms, transform 380ms cubic-bezier(.2,.8,.2,1) ${140 + dist * 24}ms` };
  };
  const cells: CalCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = i - M.lead + 1, a = anim(i);
    if (d < 1 || d > M.days) { cells.push({ kind: 'out', ...a }); continue; }
    const date = isoOf(ym.year, ym.month, d);
    if (compareDates(date, today) > 0) { cells.push({ kind: 'future', label: copy.cell.future(dateLong(date), ym.year), ...a }); continue; }
    if (state !== 'loaded') { cells.push({ kind: 'wait', loading: state === 'loading' }); continue; }
    const look = tileLook(st, date, st.hover === date), isToday = date === today;
    cells.push({ kind: 'open', date, today: isToday, label: copy.cell.open(dateLong(date), ym.year, isToday, postersCount(st, date)), ...look, ...a });
  }
  return {
    show: st.view === 'cal' || !!st.cd,
    cells,
    status: { show: state !== 'loaded', retry: state === 'error', text: state === 'loading' ? copy.month.loading(M.name) : copy.month.failed(M.name) },
  };
}
